import React, { useState, useEffect, useRef } from 'react';
import { ACTIVE_BRAND } from '../branding';
import './ResultsPage.css';
import { QRCodeSVG } from 'qrcode.react';

// Component to display cropped face based on Rekognition bounding box
function MatchedFaceImage({ imageUrl, boundingBox }) {
  const canvasRef = useRef(null);
  const [croppedImage, setCroppedImage] = useState(null);

  useEffect(() => {
    if (!imageUrl || !boundingBox) return;

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Rekognition bounding box is in ratios (0-1)
      const x = boundingBox.Left * img.width;
      const y = boundingBox.Top * img.height;
      const width = boundingBox.Width * img.width;
      const height = boundingBox.Height * img.height;

      // Set canvas size to match cropped area
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

      // Convert canvas to data URL
      setCroppedImage(canvas.toDataURL('image/jpeg'));
    };
    img.src = imageUrl;
  }, [imageUrl, boundingBox]);

  return (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {croppedImage ? (
        <img src={croppedImage} alt="Matched Face" className="comparison-img" />
      ) : (
        <div className="comparison-img-placeholder">Processing...</div>
      )}
    </>
  );
}

function ResultsPage({ classification, apiEndpoints, applicationId, selfieImage, faceComparisonResult, documentImages, digitalCard, onBackToHome, onTopUp }) {
  const [images, setImages] = useState({ dlf: null, dlb: null, face: null, signature: null });
  const [loadingImages, setLoadingImages] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchImages = async () => {
    if (hasFetched) return; // Prevent duplicate fetches
    setHasFetched(true);

    try {
      // Only fetch Face and Signature - DLF and DLB already fetched in App.js
      const [faceResponse, signatureResponse] = await Promise.all([
        fetch(apiEndpoints.getElementFile, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            application_id: applicationId,
            element_key: 'DLF_Face_1',
            processed_file: true
          })
        }),
        fetch(apiEndpoints.getElementFile, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            application_id: applicationId,
            element_key: 'DLF_Signature',
            processed_file: true
          })
        })
      ]);

      const [faceData, signatureData] = await Promise.all([
        faceResponse.json(),
        signatureResponse.json()
      ]);

      setImages({
        dlf: documentImages.dlf, // Use already-fetched DLF from App.js
        dlb: documentImages.dlb, // Use already-fetched DLB from App.js
        face: faceData.success ? `data:image/${faceData.image_format};base64,${faceData.image_data}` : null,
        signature: signatureData.success ? `data:image/${signatureData.image_format};base64,${signatureData.image_data}` : null
      });

      setLoadingImages(false);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching images:', error);
      }
      setHasFetched(false); // Reset on error to allow retry
      setLoadingImages(false);
    }
  };

  useEffect(() => {
    if (applicationId && apiEndpoints && !hasFetched) {
      fetchImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, hasFetched]);

  if (!classification) {
    return (
      <div className="results-page">
        <div className="results-content">
          <div className="results-card">
            <h2 style={{ color: ACTIVE_BRAND.colors.primary }}>No Results</h2>
            <p>No classification data was found.</p>
            <button
              className="home-button"
              onClick={onBackToHome}
              style={{
                backgroundColor: ACTIVE_BRAND.colors.buttonBg,
                color: ACTIVE_BRAND.colors.buttonText
              }}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const keyFields = [
    { key: 'First_Name', label: 'FIRST NAME' },
    { key: 'Middle_Name', label: 'MIDDLE NAME' },
    { key: 'Last_Name', label: 'LAST NAME' },
    { key: 'Date_Of_Birth', label: 'DATE OF BIRTH', icon: '📅' },
    { key: 'Licence_Number', label: 'LICENCE NUMBER', icon: '🆔' },
    { key: 'Licence_Expiry', label: 'LICENCE EXPIRY', icon: '⏰' },
    { key: 'Back_Licence_Expiry', label: 'LICENCE EXPIRY', icon: '⏰' },
    { key: 'Country_Name', label: 'COUNTRY NAME', icon: '🌏' },
    { key: 'Front_Address', label: 'ADDRESS', icon: '📍' },
    { key: 'Back_Address', label: 'CHANGE OF ADDRESS', icon: '📍' }
  ];

  return (
    <div className="results-page">
      <div className="results-content">
        <div className="results-card">
          {/* Face Verification Status - Moved to top */}
          {faceComparisonResult && faceComparisonResult.success && (
            <div className={`comparison-result-compact ${faceComparisonResult.overall_match ? 'match' : 'no-match'}`}>
              <div className="comparison-icon-compact">
                {faceComparisonResult.overall_match ? '✓' : '✗'}
              </div>
              <div className="comparison-text-compact">
                <strong>{faceComparisonResult.overall_match ? 'VERIFIED' : 'NOT VERIFIED'}</strong>
                <p>{faceComparisonResult.overall_match ? 'Selfie matches ID document photo' : 'Selfie does not match ID document photo'}</p>
              </div>
            </div>
          )}

          <h2 className="results-title">PERSONAL INFORMATION</h2>

          {/* Classification Data - Moved to top */}
          <div className="results-grid">
            {keyFields.map(field => {
              const value = classification[field.key];
              if (!value) return null;

              return (
                <div key={field.key} className="result-item">
                  <div className="result-label">
                    {field.icon && <span className="result-icon">{field.icon}</span>}
                    {field.label}
                  </div>
                  <div className="result-value">{value}</div>
                </div>
              );
            })}
          </div>

          {/* Document Images */}
          <div className="document-images-section">
            <h2 className="document-images-title">SCANNED DOCUMENTS</h2>
            <div className="document-images">
              <div className="image-container">
                <h3>Driving Licence - Front</h3>
                {loadingImages ? (
                  <div className="image-placeholder">
                    <div className="loading-spinner" style={{ borderTopColor: ACTIVE_BRAND.colors.primary }}></div>
                  </div>
                ) : images.dlf ? (
                  <img src={images.dlf} alt="Driving Licence Front" className="document-image" />
                ) : (
                  <div className="image-placeholder">No image</div>
                )}
              </div>
              <div className="image-container">
                <h3>Driving Licence - Back</h3>
                {loadingImages ? (
                  <div className="image-placeholder">
                    <div className="loading-spinner" style={{ borderTopColor: ACTIVE_BRAND.colors.primary }}></div>
                  </div>
                ) : images.dlb ? (
                  <img src={images.dlb} alt="Driving Licence Back" className="document-image" />
                ) : (
                  <div className="image-placeholder">No image</div>
                )}
              </div>
              <div className="image-container small">
                <h3>Portrait Photo</h3>
                {loadingImages ? (
                  <div className="image-placeholder small">
                    <div className="loading-spinner small"></div>
                  </div>
                ) : images.face ? (
                  <img src={images.face} alt="Portrait" className="document-image small" />
                ) : (
                  <div className="image-placeholder small">No image</div>
                )}
              </div>
              <div className="image-container small">
                <h3>Signature</h3>
                {loadingImages ? (
                  <div className="image-placeholder small">
                    <div className="loading-spinner small" style={{ borderTopColor: ACTIVE_BRAND.colors.primary }}></div>
                  </div>
                ) : images.signature ? (
                  <img src={images.signature} alt="Signature" className="document-image small" />
                ) : (
                  <div className="image-placeholder small">No image</div>
                )}
              </div>
              <div className="image-container small">
                <h3>Selfie</h3>
                {selfieImage ? (
                  <img src={selfieImage} alt="Selfie" className="document-image small" />
                ) : (
                  <div className="image-placeholder small">No selfie</div>
                )}
              </div>
            </div>
          </div>

          {/* Face Comparison Result */}
          {faceComparisonResult && (
            <div className="face-comparison-section">
              <h3 className="comparison-title">FACE VERIFICATION</h3>
              {faceComparisonResult.success ? (
                <>
                  {/* Matched Faces from Rekognition - Only show if match exists */}
                  <div className="comparison-images">
                    {/* ID Document Portrait - prefer DLF, fallback to DLB */}
                    {(faceComparisonResult.results?.dlf_comparison?.matched_face ||
                      faceComparisonResult.results?.dlb_comparison?.matched_face) && (
                        <div className="comparison-image-item">
                          <h4>ID Document Portrait</h4>
                          {faceComparisonResult.results?.dlf_comparison?.matched_face ? (
                            <MatchedFaceImage
                              imageUrl={documentImages?.dlf}
                              boundingBox={faceComparisonResult.results.dlf_comparison.matched_face.bounding_box}
                            />
                          ) : faceComparisonResult.results?.dlb_comparison?.matched_face ? (
                            <MatchedFaceImage
                              imageUrl={documentImages?.dlb}
                              boundingBox={faceComparisonResult.results.dlb_comparison.matched_face.bounding_box}
                            />
                          ) : null}
                        </div>
                      )}

                    {/* Selfie - only show if matched */}
                    {(faceComparisonResult.results?.dlf_comparison?.source_face ||
                      faceComparisonResult.results?.dlb_comparison?.source_face) &&
                      (faceComparisonResult.results?.dlf_comparison?.matched_face ||
                        faceComparisonResult.results?.dlb_comparison?.matched_face) && (
                        <>
                          <div className="comparison-vs">VS</div>
                          <div className="comparison-image-item">
                            <h4>Selfie</h4>
                            <MatchedFaceImage
                              imageUrl={selfieImage}
                              boundingBox={
                                faceComparisonResult.results.dlf_comparison?.source_face ||
                                faceComparisonResult.results.dlb_comparison?.source_face
                              }
                            />
                          </div>
                        </>
                      )}
                  </div>

                  {/* Match Percentage Badge */}
                  {faceComparisonResult.results && (
                    <div className="match-badge-container">
                      {(faceComparisonResult.results.dlf_comparison?.match ||
                        faceComparisonResult.results.dlb_comparison?.match) && (
                          <div className="match-badge">
                            <span className="match-badge-label">Match Confidence</span>
                            <span className="match-badge-value">
                              {faceComparisonResult.results.dlf_comparison?.match
                                ? `${faceComparisonResult.results.dlf_comparison.similarity}%`
                                : `${faceComparisonResult.results.dlb_comparison.similarity}%`}
                            </span>
                          </div>
                        )}
                    </div>
                  )}
                </>
              ) : (
                <div className="comparison-error">
                  Error performing face verification: {faceComparisonResult.error || 'Unknown error'}
                </div>
              )}
            </div>
          )}

          {/* Digital Card Section */}
          {ACTIVE_BRAND.digitalCard && (
            <div className="digital-card-section">
              <h3 className="digital-card-title">ADD TO DIGITAL WALLET</h3>

              {!digitalCard ? (
                <div className="digital-card-loading">
                  <div className="loading-spinner" style={{ borderTopColor: ACTIVE_BRAND.colors.primary }}></div>
                  <p>Creating your digital card...</p>
                </div>
              ) : (
                <div className="digital-card-content">
                  <p className="digital-card-description">
                    Scan the QR code below to add your digital membership card to your wallet
                  </p>
                  <div className="wallet-options">
                    {digitalCard.appleWalletUrl && (
                      <div className="wallet-option" style={{
                        borderColor: '#e0e0e0',
                        '--hover-border-color': ACTIVE_BRAND.colors.buttonBg
                      }}>
                        <h4>Apple Wallet</h4>
                        <div className="qr-code-container">
                          <QRCodeSVG
                            value={digitalCard.appleWalletUrl}
                            size={300}
                            level="H"
                            includeMargin={true}
                          />
                        </div>
                        <a
                          href={digitalCard.appleWalletUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wallet-link"
                          style={{
                            color: ACTIVE_BRAND.colors.buttonBg,
                            borderColor: ACTIVE_BRAND.colors.buttonBg,
                            '--hover-bg': ACTIVE_BRAND.colors.buttonBg,
                            '--hover-color': ACTIVE_BRAND.colors.buttonText
                          }}
                        >
                          Open Apple Wallet Link
                        </a>
                      </div>
                    )}
                    {digitalCard.googleWalletUrl && (
                      <div className="wallet-option" style={{
                        borderColor: '#e0e0e0',
                        '--hover-border-color': ACTIVE_BRAND.colors.buttonBg
                      }}>
                        <h4>Google Wallet</h4>
                        <div className="qr-code-container">
                          <QRCodeSVG
                            value={digitalCard.googleWalletUrl}
                            size={300}
                            level="H"
                            includeMargin={true}
                          />
                        </div>
                        <a
                          href={digitalCard.googleWalletUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wallet-link"
                          style={{
                            color: ACTIVE_BRAND.colors.buttonBg,
                            borderColor: ACTIVE_BRAND.colors.buttonBg,
                            '--hover-bg': ACTIVE_BRAND.colors.buttonBg,
                            '--hover-color': ACTIVE_BRAND.colors.buttonText
                          }}
                        >
                          Open Google Wallet Link
                        </a>
                      </div>
                    )}
                  </div>
                  {digitalCard.demo && (
                    <div className="demo-notice">
                      <small>⚠️ Demo Mode: Update LiteCard credentials in Lambda function</small>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {ACTIVE_BRAND.name === 'MAX Gaming' && (
            <button
              className="top-up-button"
              onClick={onTopUp}
              style={{
                backgroundColor: '#4a148c', // Purple from mockup
                color: 'white',
                marginTop: '2rem',
                width: '100%',
                padding: '1.2rem',
                border: 'none',
                borderRadius: '10px', // Match home-button radius
                fontSize: '1.2rem', // Match home-button font size
                fontWeight: '600', // Match home-button weight
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              Top Up
            </button>
          )}

          <button
            className="home-button"
            onClick={onBackToHome}
            style={{
              backgroundColor: ACTIVE_BRAND.colors.buttonBg,
              color: ACTIVE_BRAND.colors.buttonText
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResultsPage;
